import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { Layout } from './components/Layout'
import Login from './pages/Login'
import Logout from './pages/Logout'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { ThemeProvider } from './hooks/useTheme'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ToastProvider } from './components/ToastProvider'

// Lazy-loaded pages for code splitting
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })))
const PlatformsPage = lazy(() => import('./pages/PlatformsPage').then(m => ({ default: m.PlatformsPage })))
const PlansPage = lazy(() => import('./pages/PlansPage').then(m => ({ default: m.PlansPage })))
const SubscriptionsPage = lazy(() => import('./pages/SubscriptionsPage').then(m => ({ default: m.SubscriptionsPage })))
const PaymentsPage = lazy(() => import('./pages/PaymentsPage').then(m => ({ default: m.PaymentsPage })))
const APIKeyMenuPage = lazy(() => import('./pages/APIKeyMenuPage').then(m => ({ default: m.APIKeyMenuPage })))
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const CryptoPaymentPage = lazy(() => import('./pages/CryptoPaymentPage').then(m => ({ default: m.CryptoPaymentPage })))

// AGU Pages (lazy)
const AGUOverviewPage = lazy(() => import('./pages/agu/AGUOverviewPage').then(m => ({ default: m.AGUOverviewPage })))
const AGUChargePage = lazy(() => import('./pages/agu/AGUChargePage').then(m => ({ default: m.AGUChargePage })))
const AGUEscrowPage = lazy(() => import('./pages/agu/AGUEscrowPage').then(m => ({ default: m.AGUEscrowPage })))
const AGUStakingPage = lazy(() => import('./pages/agu/AGUStakingPage').then(m => ({ default: m.AGUStakingPage })))
const AGUWithdrawPage = lazy(() => import('./pages/agu/AGUWithdrawPage').then(m => ({ default: m.AGUWithdrawPage })))
const AGUAnalyticsPage = lazy(() => import('./pages/agu/AGUAnalyticsPage').then(m => ({ default: m.AGUAnalyticsPage })))

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
    </div>
  )
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}


function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
    <ToastProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Login/Logout */}
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />

          {/* Default route: redirect to /login */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* App Routes with Layout, protected */}
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <Layout>
                  <Suspense fallback={<PageLoader />}><DashboardPage /></Suspense>
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/platforms"
            element={
              <RequireAuth>
                <Layout>
                  <Suspense fallback={<PageLoader />}><PlatformsPage /></Suspense>
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/plans"
            element={
              <RequireAuth>
                <Layout>
                  <Suspense fallback={<PageLoader />}><PlansPage /></Suspense>
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <RequireAuth>
                <Layout>
                  <Suspense fallback={<PageLoader />}><SubscriptionsPage /></Suspense>
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/payments"
            element={
              <RequireAuth>
                <Layout>
                  <Suspense fallback={<PageLoader />}><PaymentsPage /></Suspense>
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/agu"
            element={
              <RequireAuth>
                <Layout>
                  <Suspense fallback={<PageLoader />}><AGUOverviewPage /></Suspense>
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/crypto-payment"
            element={
              <RequireAuth>
                <Layout>
                  <Suspense fallback={<PageLoader />}><CryptoPaymentPage /></Suspense>
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <Layout>
                  <Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/api-key"
            element={
              <RequireAuth>
                <Layout>
                  <Suspense fallback={<PageLoader />}><APIKeyMenuPage /></Suspense>
                </Layout>
              </RequireAuth>
            }
          />
          {/* AGU Sub-pages */}
          <Route
            path="/agu/charge"
            element={
              <RequireAuth>
                <Layout>
                  <Suspense fallback={<PageLoader />}><AGUChargePage /></Suspense>
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/agu/escrow"
            element={
              <RequireAuth>
                <Layout>
                  <Suspense fallback={<PageLoader />}><AGUEscrowPage /></Suspense>
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/agu/staking"
            element={
              <RequireAuth>
                <Layout>
                  <Suspense fallback={<PageLoader />}><AGUStakingPage /></Suspense>
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/agu/withdraw"
            element={
              <RequireAuth>
                <Layout>
                  <Suspense fallback={<PageLoader />}><AGUWithdrawPage /></Suspense>
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path="/agu/analytics"
            element={
              <RequireAuth>
                <Layout>
                  <Suspense fallback={<PageLoader />}><AGUAnalyticsPage /></Suspense>
                </Layout>
              </RequireAuth>
            }
          />
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ToastProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App
